import React from "react";
import Panel from "../Panel";
import { TONE_SHORT, TONE_LABEL, TONE_PILL, rowScale, pct, WAREHOUSE } from "./consumableHelpers";
import InlineAdjust from "./InlineAdjust";

// One row per consumable type, measured against its reorder line.
//
// Each bar is scaled so its reorder point sits at a fixed mark on the track (see
// rowScale), which makes "above or below the line" comparable across rows without
// pretending an antenna count and a cable count belong on the same axis.
//
// The per-location split sits on the row itself rather than behind a click, because
// "how much and where" is one question: a total of 88 means something different when
// it is all in the warehouse than when it is spread across four sites. A row still
// expands, but only to reach the actions — the figures are always visible.

// Locations shown inline before the rest collapse into "+n more". The warehouse is
// always first because it is the only holding that is free to issue.
const INLINE_LOCATIONS = 3;

function LocationSplit({ byLocation, expanded }) {
  const entries = React.useMemo(() => {
    const all = Object.entries(byLocation).filter(([, n]) => n > 0);
    all.sort(([a, an], [b, bn]) => {
      if (a === WAREHOUSE) return -1;
      if (b === WAREHOUSE) return 1;
      return bn - an;
    });
    return all;
  }, [byLocation]);

  if (entries.length === 0) {
    return <span style={{ fontSize: 12, color: "var(--text-subtle)" }}>none on hand</span>;
  }

  const shown = expanded ? entries : entries.slice(0, INLINE_LOCATIONS);
  const hidden = entries.length - shown.length;

  return (
    <span
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "2px 10px",
        justifyContent: "flex-end",
        fontSize: 12,
      }}
    >
      {shown.map(([loc, n]) => (
        <span key={loc} style={{ color: "var(--text-muted)", whiteSpace: "nowrap" }}>
          {loc}{" "}
          <span className="tnum" style={{ color: "var(--text-primary)", fontWeight: 600 }}>
            {n.toLocaleString("en-GB")}
          </span>
        </span>
      ))}
      {hidden > 0 && (
        <span style={{ color: "var(--text-subtle)", whiteSpace: "nowrap" }}>
          +{hidden} more
        </span>
      )}
    </span>
  );
}

export default function StockPanel({
  rows,
  expandedId,
  onToggle,
  locations,
  quantities,
  onAdjust,
  availableCount,
  canEdit,
}) {
  return (
    <Panel
      title="Stock"
      sub={rows.length + " type" + (rows.length === 1 ? "" : "s")}
      bodyStyle={rows.length ? { padding: 0 } : undefined}
    >
      {rows.length === 0 ? (
        <div className="empty-state">
          <div className="empty-title">No consumable types</div>
          <div className="empty-desc">
            Add one in <strong>Settings &rsaquo; Consumables</strong>.
          </div>
        </div>
      ) : (
        <div>
          {rows.map(r => {
            const short = r.tone === TONE_SHORT;
            const fill =
              r.tone === TONE_SHORT
                ? "var(--danger-text)"
                : r.tone === "low"
                ? "var(--warning-text)"
                : "var(--success-text)";
            const expanded = expandedId === r.id;
            const free = r.tracked ? availableCount(r.id) : null;
            const scale = rowScale(r);

            return (
              <div
                key={r.id}
                id={"consumable-row-" + r.id}
                style={{
                  borderBottom: "1px solid var(--border-subtle)",
                  background: expanded ? "var(--surface-raised)" : "transparent",
                }}
              >
                <button
                  type="button"
                  onClick={() => onToggle(r.id)}
                  aria-expanded={expanded}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    font: "inherit",
                    cursor: "pointer",
                    padding: "14px 16px",
                    border: "none",
                    background: "transparent",
                  }}
                >
                  {/* Name · total · state · where it is held */}
                  <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: "var(--text-primary)",
                        minWidth: 140,
                      }}
                    >
                      {r.name}
                    </span>
                    <span
                      className="tnum"
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: short ? "var(--danger-text)" : "var(--text-primary)",
                      }}
                    >
                      {r.total.toLocaleString("en-GB")}
                    </span>
                    <span className={TONE_PILL[r.tone]} style={{ flexShrink: 0 }}>
                      {TONE_LABEL[r.tone]}
                    </span>
                    <span style={{ flex: 1, minWidth: 120, textAlign: "right" }}>
                      <LocationSplit byLocation={r.byLocation} expanded={expanded} />
                    </span>
                  </div>

                  {/* The bar only means anything once a reorder point is set. */}
                  {r.reorderPoint > 0 ? (
                    <div style={{ marginTop: 10 }}>
                      <div
                        style={{
                          position: "relative",
                          height: 8,
                          borderRadius: 9999,
                          background: "var(--surface-inset)",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            height: "100%",
                            width: pct(r.total, scale),
                            background: fill,
                            borderRadius: 9999,
                          }}
                        />
                      </div>
                      {/* The reorder tick sits outside the clipped bar so it stays
                          visible even where the fill has not reached it. */}
                      <div style={{ position: "relative", height: 6 }}>
                        <span
                          aria-hidden="true"
                          style={{
                            position: "absolute",
                            left: pct(r.reorderPoint, scale),
                            top: -10,
                            width: 2,
                            height: 14,
                            background: "var(--text-primary)",
                            opacity: 0.55,
                          }}
                        />
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: 10,
                          marginTop: 4,
                          fontSize: 12,
                          color: "var(--text-muted)",
                        }}
                      >
                        <span style={{ flex: 1 }}>
                          reorder at {r.reorderPoint}
                          {free !== null && <> · {free.toLocaleString("en-GB")} free to issue</>}
                        </span>
                        <span
                          className="tnum"
                          style={{
                            color: short ? "var(--danger-text)" : "var(--text-muted)",
                            fontWeight: short ? 600 : 400,
                          }}
                        >
                          {r.gap < 0 ? Math.abs(r.gap) + " short" : r.gap + " above"}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div style={{ marginTop: 6, fontSize: 12, color: "var(--text-subtle)" }}>
                      No reorder point set
                      {free !== null && <> · {free.toLocaleString("en-GB")} free to issue</>}
                    </div>
                  )}
                </button>

                {/* Expanded: the actions for this type, in reach of the row you
                    clicked rather than in a form that asks you to name it again. */}
                {expanded && (
                  <div style={{ padding: "0 16px 14px" }}>
                    {r.tracked ? (
                      <div style={{ fontSize: 12, lineHeight: 1.5, color: "var(--text-muted)" }}>
                        Tracked individually — every unit carries its own QR code and moves on
                        its own. Add units with <strong>Set up units</strong>; scanning one at
                        another site moves it there.
                      </div>
                    ) : canEdit ? (
                      <InlineAdjust
                        row={r}
                        locations={locations}
                        quantities={quantities}
                        onAdjust={onAdjust}
                      />
                    ) : (
                      <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                        Counted by quantity. You do not have permission to adjust stock.
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
}
